import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Params, SCREEN_SIZE, USERSROLE } from 'types';

import { MainHeaderContainer, MainContainer } from 'layouts/components';
import { ReferralDetailsView } from './components/referralDetailsView';
import {
  Button,
  ContentSwitcher,
  Switch,
  Modal,
  Link,
  ToastNotification,
  InlineLoading
} from 'carbon-components-react';
import { ListView, MapView, SearchFilter } from './components';
import { ListBulleted16, Map16 } from '@carbon/icons-react';
import { currentUser, selectScreenSize } from 'redux/session/selectors';
import {
  selectReferralSearchFilters,
  selectForwardReferralExternalId,
  selectForwardReferralIsListView,
  fetchReferralHistory,
  clearAllReferralState,
  selectReferrals,
  selectReprocess,
  reprocessReferralsClear,
  reprocessReferrals,
  showReprocessReferralsModal
} from 'redux/referrals';
import { fetchLodgeEnquiryInit } from 'redux/lodgeEnquiry';
import { clearAllEnquiryState } from 'redux/enquiries';
import { useComponentMount } from 'utilities/hooks.util';
import { selectAdminRangeText } from 'redux/dashboard/selectors';

const filterStringReducer = (accumulator: string[], currentValue: any) => {
  const [key, value] = currentValue;

  switch (key) {
    case 'status':
      if (value.length > 0) accumulator.push(`${key}:${value.join('|')}`);
      return accumulator;
    case 'requestorFullName':
      if (value) accumulator.push(`requestorFullName:%${value}%`);
      return accumulator;
    case 'requestorEmail':
      if (value) accumulator.push(`requestorEmail:%${value}%`);
      return accumulator;
    case 'isSandboxTest':
      accumulator.push(`${key}:${value}`);
      return accumulator;
    default:
      if (value) accumulator.push(`${key}:${value}`);
      return accumulator;
  }
};

const filterObjectReducer = (accumulator: any, currentValue: any) => {
  const [key, value] = currentValue;

  switch (key) {
    case 'status':
      if (value.length > 0) accumulator[key] = value.join('|');
      return accumulator;
    case 'requestorFullName':
      if (value) accumulator[key] = `%${value}%`;
      return accumulator;
    case 'requestorEmail':
      if (value) accumulator[key] = `%${value}%`;
      return accumulator;
    case 'isSandboxTest':
      accumulator[key] = value;
      return accumulator;
    default:
      if (value) accumulator[key] = value;
      return accumulator;
  }
};

export const ReferralHistory: React.FC<any> = () => {
  // INIT
  const overallContainerRef = useRef(null);
  const PAGE_LIMIT = 10;
  const PAGE_OFFSET = 0;
  const dispatch = useDispatch();
  const user: any = useSelector(currentUser);
  const screenSize = useSelector(selectScreenSize);
  const searchFillter = useSelector(selectReferralSearchFilters);
  const forwardExternalId = useSelector(selectForwardReferralExternalId);
  const forwardIsListView = useSelector(selectForwardReferralIsListView);
  const dashboardAdminRangeText = useSelector(selectAdminRangeText);
  const { count: referralsCount = 0, queryCreatedBefore, queryCreatedAfter } =
    useSelector(selectReferrals) || {};
  const {
    modalType,
    isLoading: reprocessIsLoading,
    status: reprocessStatus
  } = useSelector(selectReprocess);
  const [filterString, setFilterString] = useState<string>('');
  const [referralList, setReferralList] = useState<any[]>([]);
  const [pageOffset, setPageOffset] = useState<number>(PAGE_OFFSET);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(PAGE_LIMIT);
  const [isPannelExpanded, setIsPannelExpanded] = useState<boolean>(false);
  const [isListView, setIsListView] = useState<boolean>(false);
  const [isFirstFilterReady, setIsFirstFilterReady] = useState<boolean>(
    forwardExternalId ? true : false
  );
  const [orderBy, setOrderBy] = useState<string>('-externalId');
  const [currentReferralId, setCurrentReferralId] = useState<
    number | undefined
  >();
  const [initialDateRangeText, setInitialDateRangeSet] = useState<string>(
    dashboardAdminRangeText
  );
  const [isEnableReprocess, setIsEnableReprocess] = useState<boolean>(false);
  const [selectedRowCount, setSelectedRowCount] = useState<number>(0);
  const selectedRowRef = useRef<any[]>([]);
  const filterObjectRef = useRef<any>({});
  const isSelectedAllAndHaveMore =
    isEnableReprocess &&
    referralList.length === selectedRowCount &&
    referralsCount > selectedRowCount &&
    referralList.length;

  const onClickCloseButton = () => {
    setIsPannelExpanded(!isPannelExpanded);
  };
  const rightPanelExpandHandler = (val: number) => {
    setCurrentReferralId(val);
    setIsPannelExpanded(!isPannelExpanded);
  };

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageLimit(pageSize);
  };

  useEffect(() => {
    if (dashboardAdminRangeText) {
      setInitialDateRangeSet(dashboardAdminRangeText);
    } else {
      setInitialDateRangeSet('');
    }
  }, [dashboardAdminRangeText]);

  useEffect(() => {
    setReferralList([]);
    const queryParams: Params = {
      fields: `*`,
      include: 'Enquiry',
      orderBy,
      limit: pageLimit,
      offset: pageOffset,
      filter: filterString
    };

    const canFetchReferralHistory = isListView && queryParams && filterString;

    if (canFetchReferralHistory) {
      dispatch(fetchReferralHistory(queryParams));
    }
  }, [dispatch, pageOffset, pageLimit, filterString, orderBy, isListView]);

  useEffect(() => {
    if (reprocessStatus !== 'NONE') {
      setTimeout(() => dispatch(reprocessReferralsClear()), 5500);
    }

    if (reprocessStatus === 'SUCCESS') {
      setReferralList([]);
      setIsEnableReprocess(false);
      dispatch(
        fetchReferralHistory({
          fields: `*`,
          include: 'Enquiry',
          orderBy,
          limit: pageLimit,
          offset: pageOffset,
          filter: filterString
        })
      );
    }
  }, [reprocessStatus, dispatch, pageLimit, pageOffset, filterString, orderBy]);

  useEffect(() => {
    setPageOffset(PAGE_OFFSET);
  }, [orderBy]);

  useEffect(() => {
    const filterArr = Object.entries({
      ...searchFillter,
      organisationId: user.organisationId || '',
      isSandboxTest: false
    });
    setPageOffset(PAGE_OFFSET);

    if (user?.organisationId && searchFillter) {
      const finalFilterArr = filterArr.reduce(filterStringReducer, []);
      filterObjectRef.current = filterArr.reduce(filterObjectReducer, {});
      setFilterString(finalFilterArr.join(','));
    }
  }, [user, searchFillter]);

  useEffect(() => {
    if (!isListView) {
      setReferralList([]);
      setPageOffset(PAGE_OFFSET);
      setCurrentPage(1);
    }
  }, [isListView, dispatch]);

  useComponentMount(() => {
    forwardExternalId && rightPanelExpandHandler(forwardExternalId);
    forwardIsListView && setIsListView(true);
    dispatch(fetchLodgeEnquiryInit());
    return () => {
      dispatch(clearAllEnquiryState());
      dispatch(clearAllReferralState());
    };
  });

  return (
    <div
      id="referralHistory"
      data-testid="referralHistory"
      className={`container--wrapper referralHistory history-page 
    ${isPannelExpanded ? 'opened-modal' : ''}`}
      ref={overallContainerRef}
    >
      <MainHeaderContainer enableHeaderGlobalBar={true}>
        <div>
          <Modal
            open={isPannelExpanded}
            onRequestClose={onClickCloseButton}
            passiveModal
            size="lg"
            className="referral-details-modal"
          >
            <ReferralDetailsView
              isModal={true}
              panelHeading={currentReferralId || ''}
              onClickClose={onClickCloseButton}
            />
          </Modal>
        </div>
      </MainHeaderContainer>

      <MainContainer>
        <section
          className={`history-page--header${isListView ? ' is-list-view' : ''}`}
        >
          <div className="page--heading">
            <h4 className="bx--data-table-header__title">Referral history</h4>
            {screenSize !== SCREEN_SIZE.SM && screenSize !== SCREEN_SIZE.MD && (
              <p className="bx--data-table-header__description">
                Review details of current and older referrals for your
                organisation
              </p>
            )}
          </div>
          <div className="page--controls">
            {user?.role === USERSROLE.AOADMIN && isListView && (
              <Button
                className="bx--reprocess-button"
                kind={
                  isEnableReprocess
                    ? selectedRowCount > 0
                      ? 'primary'
                      : 'secondary'
                    : 'primary'
                }
                data-testid="enable-reprocess"
                onClick={
                  selectedRowCount > 0
                    ? () => dispatch(showReprocessReferralsModal('SELECTED'))
                    : () => setIsEnableReprocess(!isEnableReprocess)
                }
                disabled={
                  referralsCount === 0 ||
                  referralList.length === 0 ||
                  reprocessIsLoading
                }
              >
                {isEnableReprocess
                  ? selectedRowCount
                    ? `Resend selected referral(s)`
                    : 'Cancel resend'
                  : `Resend referral(s)`}
              </Button>
            )}

            <SearchFilter
              open={!isFirstFilterReady || undefined}
              disabledAllExceptApply={!isFirstFilterReady}
              onApply={() => {
                selectedRowRef.current = [];
                setIsEnableReprocess(false);
                setIsFirstFilterReady(true);
              }}
              mainContainerRef={overallContainerRef}
              isListView={isListView}
              rangeText={initialDateRangeText}
            />
            <div className="page--switcher">
              {screenSize === SCREEN_SIZE.SM ? (
                <>
                  <Button
                    size="field"
                    className={`icon-button-switcher ${
                      !isListView ? 'icon-button-switcher--selected' : ''
                    }`}
                    hasIconOnly
                    renderIcon={Map16}
                    tooltipAlignment="center"
                    tooltipPosition="bottom"
                    iconDescription="Map view"
                    onClick={() => setIsListView(false)}
                  />
                  <Button
                    size="field"
                    className={`icon-button-switcher ${
                      isListView ? 'icon-button-switcher--selected' : ''
                    }`}
                    hasIconOnly
                    renderIcon={ListBulleted16}
                    tooltipAlignment="center"
                    tooltipPosition="bottom"
                    iconDescription="List view"
                    onClick={() => setIsListView(true)}
                  />
                </>
              ) : (
                <ContentSwitcher selectedIndex={isListView ? 1 : 0}>
                  <Switch
                    name={'map-view'}
                    text="Map view"
                    onClick={() => setIsListView(false)}
                  />
                  <Switch
                    name={'list-view'}
                    text="List view"
                    onClick={() => setIsListView(true)}
                  />
                </ContentSwitcher>
              )}
            </div>
          </div>
        </section>
        <section className="history-page--view-switcher">
          {isListView ? (
            <>
              {isSelectedAllAndHaveMore ? (
                <div className="selected-rows-count">
                  <span>
                    All {pageLimit} referrals on this page are selected.
                  </span>
                  &nbsp;
                  <Link
                    className="pointer"
                    inline
                    onClick={() => dispatch(showReprocessReferralsModal('ALL'))}
                    disabled={reprocessIsLoading}
                  >
                    Resend all {referralsCount} filtered referrals
                  </Link>
                </div>
              ) : null}
              <ListView
                isEnableReprocess={isEnableReprocess}
                referralList={referralList}
                setReferralList={setReferralList}
                setOrderBy={setOrderBy}
                pageSize={pageLimit}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                selectedId={(val: number) => rightPanelExpandHandler(val)}
                selectedRowRef={selectedRowRef}
                setSelectedRowCount={setSelectedRowCount}
              />
            </>
          ) : (
            <MapView
              selectedId={(val: number) => rightPanelExpandHandler(val)}
              isMapVisible={isFirstFilterReady}
            />
          )}
        </section>
      </MainContainer>
      <Modal
        modalHeading="Resend referral(s)"
        onRequestClose={() => dispatch(showReprocessReferralsModal('NONE'))}
        onRequestSubmit={() =>
          dispatch(
            reprocessReferrals(
              modalType === 'ALL'
                ? {
                    query: Object.assign(filterObjectRef.current, {
                      createdBefore: queryCreatedBefore,
                      createdAfter: queryCreatedAfter
                    })
                  }
                : { ids: selectedRowRef.current }
            )
          )
        }
        onSecondarySubmit={() => {
          !reprocessIsLoading && dispatch(showReprocessReferralsModal('NONE'));
        }}
        preventCloseOnClickOutside
        open={modalType !== 'NONE'}
        primaryButtonText={
          reprocessIsLoading ? (
            <InlineLoading description="Confirm" status="active" />
          ) : (
            'Confirm'
          )
        }
        secondaryButtonText="Cancel"
        primaryButtonDisabled={reprocessIsLoading}
        size="sm"
      >
        <p>
          You’re about to resend{' '}
          <span className="bold-text">
            {modalType === 'ALL'
              ? `${referralsCount} referrals that match your filters`
              : modalType === 'SELECTED'
              ? `${selectedRowCount} selected referral${
                  selectedRowCount > 1 ? 's' : ''
                }`
              : ''}
            .
          </span>
        </p>
        <br />
        <p>
          Your referrals may take some time to resend. The last update date and
          time will refresh once each one is resent.
        </p>
        <br />
        {modalType === 'ALL' ? (
          <p>Click confirm to continue.</p>
        ) : isSelectedAllAndHaveMore ? (
          <>
            <p>Click confirm to continue or</p>
            <p>
              <Link
                className="pointer"
                disabled={reprocessIsLoading}
                inline
                onClick={() => dispatch(showReprocessReferralsModal('ALL'))}
              >
                Resend all referrals that match your filters
              </Link>
            </p>
          </>
        ) : (
          <p>Click confirm to continue.</p>
        )}
      </Modal>
      {reprocessStatus !== 'NONE' ? (
        <div className="reprocess-toast">
          <ToastNotification
            lowContrast
            caption={
              reprocessStatus === 'SUCCESS'
                ? 'Referral(s) resent successfully'
                : 'Failed to resend referral(s)'
            }
            kind={reprocessStatus === 'SUCCESS' ? 'success' : 'error'}
            title={''}
            onCloseButtonClick={() => dispatch(reprocessReferralsClear())}
            timeout={5000}
          />
        </div>
      ) : null}
    </div>
  );
};

ReferralHistory.displayName = 'ReferralHistory';

export default React.memo(ReferralHistory);
